import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Campaign, LinkAnalytics, ShortLink } from '@shared/types';
import { Spinner } from '../components/Spinner';
import { Sparkline } from '../components/Sparkline';
import { MetricCard } from '../components/Card';
import { useToast } from '../lib/toast';
import { formatNumber, formatRelative } from '../lib/format';

interface DetailResponse {
  campaign: Campaign;
  links: ShortLink[];
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [analytics, setAnalytics] = useState<LinkAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<DetailResponse>(`/api/campaigns/${id}`)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    api
      .get<LinkAnalytics>(`/api/campaigns/${id}/analytics`)
      .then(setAnalytics)
      .catch(() => {
        /* non-blocking */
      });
  }, [id]);

  if (error)
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
        {error}
      </div>
    );
  if (!data || !id)
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size={28} />
      </div>
    );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/app/campaigns" className="text-sm text-brand-600 dark:text-brand-300">
            ← All campaigns
          </Link>
          <h1 className="text-2xl font-bold tracking-tight mt-2">{data.campaign.name}</h1>
          {data.campaign.description && (
            <p className="text-sm text-ink-500 dark:text-ink-400 mt-1 max-w-2xl">
              {data.campaign.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-danger"
            onClick={async () => {
              if (!confirm('Delete this campaign? Existing links will be unlinked.')) return;
              try {
                await api.del(`/api/campaigns/${id}`);
                toast.success('Campaign deleted');
                navigate('/app/campaigns');
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed');
              }
            }}
          >
            Delete
          </button>
        </div>
      </header>

      {analytics && (
        <>
          <section className="grid gap-4 md:grid-cols-3">
            <MetricCard label="Successful clicks" value={formatNumber(analytics.totals.total)} />
            <MetricCard label="Unique visitors" value={formatNumber(analytics.totals.unique)} />
            <MetricCard label="Linked URLs" value={formatNumber(data.links.length)} />
          </section>
          <section className="card p-5">
            <h2 className="font-semibold mb-3">Click activity</h2>
            <Sparkline
              data={analytics.timeseries.map((d) => ({ date: d.date, total: d.totalClicks }))}
              height={140}
              showAxis
            />
          </section>
        </>
      )}

      <section className="space-y-3">
        <h2 className="font-semibold">Linked short links</h2>
        {data.links.length === 0 ? (
          <p className="text-sm text-ink-500 dark:text-ink-400">
            No links yet. Assign a campaign to a link from its detail page.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Destination</th>
                  <th>Clicks</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.links.map((l) => (
                  <tr key={l.id}>
                    <td className="font-mono text-xs">
                      <Link
                        to={`/app/links/${l.id}`}
                        className="text-brand-600 dark:text-brand-300 hover:underline"
                      >
                        {l.code}
                      </Link>
                    </td>
                    <td className="truncate max-w-md">{l.destinationUrl}</td>
                    <td>{formatNumber(l.clickCount)}</td>
                    <td className="text-ink-500 dark:text-ink-400 text-xs">
                      {formatRelative(l.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
